import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSelector } from "react-redux";
import { buildZodSchema } from "../../services/dynamicForms/fieldSchema";
import useSendMoneyQuote from "./useSendMoneyQuote";

// types
import type { FieldDescriptor } from "../../types/components/dynamicForms";
import type { RootState } from "../../types/redux";

export const SEND_AMOUNT_FIELD = "sendAmount";

const fmt = (n: number): string =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const useTransferDetailsForm = () => {
  const limits = useSelector((state: RootState) => state.sendMoney.limits);

  const { loading, quote, walletOperatorDisplay, hasError, requestQuote } =
    useSendMoneyQuote();

  // per-transaction min/max are field constraints (they judge the amount's
  // value directly) and flow through buildZodSchema.
  const fields = useMemo<FieldDescriptor[]>(() => {
    const constraints: FieldDescriptor["constraints"] = { required: true };

    if (limits) {
      constraints.minValue = limits.transactionMinLimit;
      constraints.maxValue = limits.transactionMaxLimit;
      constraints.minValueMessage = `Minimum amount is ${fmt(
        limits.transactionMinLimit
      )} USD`;
      constraints.maxValueMessage = `Maximum amount is ${fmt(
        limits.transactionMaxLimit
      )} USD`;
    }

    return [
      {
        name: SEND_AMOUNT_FIELD,
        kind: "decimal",
        label: "You send",
        placeholder: "0.00",
        constraints,
      },
    ];
  }, [limits]);

  // The two rolling-24h caps are business rules about the window, not about the
  // amount's intrinsic value, so they're refined on after buildZodSchema with
  // their own copy. Both still surface through zodResolver/resolveFieldError.
  const schema = useMemo(() => {
    const base = buildZodSchema(fields);
    if (!limits) return base;

    let refinedSchema = base;

    // count cap: no transfer of any amount is allowed
    if (limits.rolling24HourCountRemaining <= 0) {
      const message = `You've reached your ${limits.rolling24HourCountLimit}-transfer limit for now.`;
      refinedSchema = refinedSchema.superRefine((values, ctx) => {
        const raw = ((values as Record<string, unknown>)[SEND_AMOUNT_FIELD] ?? "")
          .toString()
          .trim();
        if (raw === "") return;
        ctx.addIssue({
          code: "custom",
          path: [SEND_AMOUNT_FIELD],
          message
        });
      }) as typeof base;
    }

    // rolling amount cap: distinct copy from the per-transaction max
    const remaining = limits.rolling24HourAmountRemaining;
    refinedSchema = refinedSchema.superRefine((values, ctx) => {
      const raw = ((values as Record<string, unknown>)[SEND_AMOUNT_FIELD] ?? "")
        .toString()
        .trim();
      if (raw === "") return;
      const n = Number(raw);
      if (Number.isNaN(n)) return;
      if (n > remaining) {
        ctx.addIssue({
          code: "custom",
          path: [SEND_AMOUNT_FIELD],
          message: `You can send up to ${fmt(remaining)} USD right now.`
        });
      }
    }) as typeof base;

    return refinedSchema;
  }, [fields, limits]);

  const form = useForm<Record<string, unknown>>({
    resolver: zodResolver(schema),
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: { [SEND_AMOUNT_FIELD]: "" },
  });

  const { watch, trigger, formState } = form;

  useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name && name !== SEND_AMOUNT_FIELD) return;
      const raw = (values[SEND_AMOUNT_FIELD] ?? "").toString().trim();

      if (raw === "") {
        requestQuote(null);
        return;
      }

      trigger(SEND_AMOUNT_FIELD).then((isValid) => {
        requestQuote(isValid ? Number(raw) : null);
      });
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger, requestQuote]);

  // re-validate an already-entered amount when limits arrive/change
  useEffect(() => {
    if (formState.isDirty) trigger(SEND_AMOUNT_FIELD);
  }, [schema, trigger, formState.isDirty]);

  const currentAmount = useWatch({ control: form.control, name: SEND_AMOUNT_FIELD });

  const showRolling24hLink = useMemo(() => {
    if (!limits) return false;
    const raw = (currentAmount ?? "").toString().trim();
    if (raw === "") return false;
    if (limits.rolling24HourCountRemaining <= 0) return true;
    const n = Number(raw);
    return !Number.isNaN(n) && n > limits.rolling24HourAmountRemaining;
  }, [currentAmount, limits]);

  return {
    form,
    fields,
    loading,
    quote,
    walletOperatorDisplay,
    hasError,
    showRolling24hLink,
  };
};